import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const evento = {
  name: "O Futuro do RH",
  type: "Palestra",
  desc: "Tendências, tecnologias e transformações que vão redefinir a gestão de pessoas nos próximos anos.",
  icon: "ri-rocket-2-line",
  image: "https://storage.readdy-site.link/project_files/9309375e-f328-4fba-b8d7-64d73f28a855/b33ff6a9-fafe-4a93-86fa-879bba1b73ef_compressed_IMG_8388r.webp",
};

const typeColors: Record<string, string> = {
  "Palestra": "bg-white/10 text-white",
};

export default function MentoresRH() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".ment-item").forEach((item, i) => {
              setTimeout(() => item.classList.add("ment-visible"), i * 80);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="mentores-rh" className="py-16 lg:py-20 bg-[#0D0D0D] overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
          <div className="ment-item opacity-0 translate-y-6 transition-all duration-500">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-[#FBC52D]" />
              <span className="text-[#FBC52D] text-xs font-semibold tracking-widest uppercase">
                Eventos e Comunidade
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
              Os Mentores dos Líderes
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed">
              Levamos melhorias práticas para líderes e gestores através de um ecossistema de educação e networking. Encontros, palestras, almoços, jantares e imersões que transformam a forma como os líderes de pessoas pensam e atuam.
            </p>
            <Link
              to="/servicos/mentores-rh"
              className="whitespace-nowrap inline-flex items-center gap-1.5 mt-5 text-[#FBC52D] text-sm font-semibold hover:text-[#d4a800] transition-colors cursor-pointer"
            >
              Conheça o Mentores RH <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="ment-item opacity-0 translate-y-6 transition-all duration-500 grid grid-cols-2 gap-4">
            {[
              { icon: "ri-calendar-event-line", value: "10+", label: "Eventos por ano" },
              { icon: "ri-group-line", value: "2.000+", label: "Profissionais de RH" },
              { icon: "ri-map-pin-line", value: "SP & RJ", label: "Principais cidades" },
              { icon: "ri-award-line", value: "C-Level", label: "Nível dos participantes" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/5 border border-white/8 rounded-xl p-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FBC52D]/15 mb-3">
                  <i className={`${stat.icon} text-[#FBC52D] text-sm`} />
                </div>
                <div className="text-white font-bold text-xl mb-0.5">{stat.value}</div>
                <div className="text-gray-500 text-xs">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Event Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Event Card */}
          <div className="ment-item opacity-0 translate-y-6 transition-all duration-500 lg:col-span-1">
            <div className="bg-[#FBC52D]/10 border border-[#FBC52D]/30 rounded-xl px-4 py-3 flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#FBC52D]/20 shrink-0">
                <i className={`${evento.icon} text-xs text-[#FBC52D]`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-white truncate">
                  {evento.name}
                </p>
                <p className="text-xs text-[#FBC52D]">
                  {evento.type}
                </p>
              </div>
              <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#FBC52D] shrink-0" />
            </div>
          </div>

          {/* Right: Event Detail */}
          <div className="ment-item opacity-0 translate-y-6 transition-all duration-500 lg:col-span-2">
            <div className="relative rounded-2xl overflow-hidden h-72 lg:h-80 mb-5">
              <img
                src={evento.image}
                alt={evento.name}
                className="w-full h-full object-cover object-[50%_35%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

              <div className="absolute top-5 left-5">
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${typeColors[evento.type] || "bg-white/10 text-white"}`}>
                  {evento.type}
                </span>
              </div>

              <div className="absolute bottom-5 left-5 right-5">
                <h3 className="text-white font-bold text-2xl mb-2">{evento.name}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{evento.desc}</p>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold text-sm mb-1">Participe do próximo evento</p>
                <p className="text-gray-400 text-xs">Vagas limitadas. Inscreva-se com antecedência.</p>
              </div>
              <a
                href="https://wa.me/5511936189998"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap bg-[#FBC52D] text-black font-semibold px-6 py-2.5 rounded-full hover:bg-[#d4a800] transition-colors text-sm cursor-pointer inline-block"
              >
                Quero participar
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ment-visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
}